import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../utils/apiConfig';
import axios from 'axios';
import Modal from '../components/Modal';
import QuizCreationModal from './QuizCreationModal';
import styles from './QuizManagement.module.css';

const QuizManagement = ({ students = [], yearGroups = [] }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [assigningQuiz, setAssigningQuiz] = useState(false);
  const [selectedYearGroup, setSelectedYearGroup] = useState('all');

  // Ensure year group is reset when modal opens
  useEffect(() => {
    if (showAssignModal) {
      setSelectedYearGroup('all');
    }
  }, [showAssignModal]);
  const [selectedStudentsForQuizAssignment, setSelectedStudentsForQuizAssignment] = useState([]);
  const [assignmentDueDate, setAssignmentDueDate] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [deletingQuiz, setDeletingQuiz] = useState(false);
  
  // Save to Library modal state
  const [showSaveToLibraryModal, setShowSaveToLibraryModal] = useState(false);
  const [quizToSave, setQuizToSave] = useState(null);
  const [libraryFormData, setLibraryFormData] = useState({
    tags: '',
    category: 'academic',
    isPublic: false,
    isShared: false
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuizForView, setSelectedQuizForView] = useState(null);
  const [selectedQuizForEdit, setSelectedQuizForEdit] = useState(null);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      setError(''); // Clear any previous errors
      const response = await axios.get(createApiUrl('/quiz'), {
        withCredentials: true
      });
      
      if (response.data.success) {
        const quizzesData = response.data.data?.quizzes || response.data.data || response.data.quizzes || [];
        setQuizzes(Array.isArray(quizzesData) ? quizzesData : []);
      } else {
        // If success is false but we got a response, it might just be empty
        setQuizzes([]);
      }
    } catch (err) {
      // Only show error for actual network/server errors, not for empty results
      if (err.response && err.response.status >= 500) {
        // Server error (500+)
        console.error('Error fetching quizzes:', err);
        setError('Failed to fetch quizzes');
      } else if (err.response && err.response.status === 404) {
        // Not found - just set empty array, no error
        setQuizzes([]);
      } else if (!err.response) {
        // Network error
        console.error('Network error fetching quizzes:', err);
        setError('Failed to fetch quizzes. Please check your connection.');
      } else {
        // Other client errors (400-499) - likely just no quizzes
        setQuizzes([]);
      }
    } finally {
      setLoading(false);
    }
  };


  const getFilteredStudents = () => {
    return students.filter(student => 
      selectedYearGroup === 'all' || student.yearGrade === selectedYearGroup
    );
  };

  const handleStudentSelectionForQuizAssignment = (student) => {
    setSelectedStudentsForQuizAssignment(prev => {
      const isSelected = prev.find(s => s._id === student._id);
      if (isSelected) {
        return prev.filter(s => s._id !== student._id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleAssignQuiz = async () => {
    if (selectedStudentsForQuizAssignment.length === 0) {
      setError('Please select at least one student');
      return;
    }

    setAssigningQuiz(true);
    setError('');

    try {
      const response = await axios.post(createApiUrl(`/api/quiz/${selectedQuiz._id}/assign`), {
        studentIds: selectedStudentsForQuizAssignment.map(s => s._id),
        dueDate: assignmentDueDate || null
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setSuccess(`Quiz assigned to ${selectedStudentsForQuizAssignment.length} student(s)`);
        setShowAssignModal(false);
        setSelectedStudentsForQuizAssignment([]);
        setAssignmentDueDate('');
        fetchQuizzes(); // Refresh quizzes to show updated assignment counts
      } else {
        setError(response.data.message || 'Failed to assign quiz');
      }
    } catch (err) {
      console.error('Error assigning quiz:', err);
      setError('Failed to assign quiz');
    } finally {
      setAssigningQuiz(false);
    }
  };

  const handleDeleteQuiz = (quiz) => {
    setQuizToDelete(quiz);
    setShowDeleteModal(true);
  };

  const confirmDeleteQuiz = async () => {
    if (!quizToDelete) return;

    setDeletingQuiz(true);
    setError('');

    try {
      const response = await axios.delete(createApiUrl(`/api/quiz/${quizToDelete._id}`), {
        withCredentials: true
      });

      if (response.data.success) {
        setSuccess('Quiz deleted successfully');
        setError(''); // Clear any previous errors
        fetchQuizzes(); // Refresh the quiz list
        setShowDeleteModal(false);
        setQuizToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete quiz');
        setSuccess(''); // Clear any previous success messages
      }
    } catch (err) {
      console.error('Error deleting quiz:', err);
      setError(err.response?.data?.message || 'Failed to delete quiz');
      setSuccess(''); // Clear any previous success messages
    } finally {
      setDeletingQuiz(false);
    }
  };

  const cancelDeleteQuiz = () => {
    setShowDeleteModal(false);
    setQuizToDelete(null);
  };

  const handleViewQuiz = async (quiz) => {
    try {
      const response = await axios.get(createApiUrl(`/api/quiz/${quiz._id}`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        setSelectedQuizForView(response.data.data.quiz);
        setShowViewModal(true);
      } else {
        setError('Failed to fetch quiz details');
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError('Failed to fetch quiz details');
    }
  };

  const handleEditQuiz = async (quiz) => {
    try {
      const response = await axios.get(createApiUrl(`/api/quiz/${quiz._id}`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        const quizData = response.data.data.quiz || response.data.data;
        setSelectedQuizForEdit(quizData);
        setShowEditModal(true);
      } else {
        setError('Failed to fetch quiz details for editing');
      }
    } catch (err) {
      console.error('Error fetching quiz for editing:', err);
      setError('Failed to fetch quiz details for editing');
    }
  };

  const handleQuizUpdated = () => {
    setShowEditModal(false);
    setSelectedQuizForEdit(null);
    fetchQuizzes(); // Refresh the quiz list
    setSuccess('Quiz updated successfully');
  };

  const handleDuplicateQuiz = async (quiz) => {
    try {
      const response = await axios.post(createApiUrl(`/api/quiz/${quiz._id}/duplicate`), {}, {
        withCredentials: true
      });

      if (response.data.success) {
        setSuccess('Quiz duplicated successfully');
        fetchQuizzes();
      } else {
        setError(response.data.message || 'Failed to duplicate quiz');
      }
    } catch (err) {
      console.error('Error duplicating quiz:', err);
      setError('Failed to duplicate quiz');
    }
  };

  const handleSaveToLibrary = (quiz) => {
    setQuizToSave(quiz);
    setLibraryFormData({
      tags: quiz.tags ? quiz.tags.join(', ') : '',
      category: 'academic',
      isPublic: false,
      isShared: false
    });
    setShowSaveToLibraryModal(true);
  };

  const handleLibraryFormChange = (field, value) => {
    setLibraryFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmSaveToLibrary = async () => {
    if (!quizToSave) return;

    try {
      const tagsArray = libraryFormData.tags 
        ? libraryFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      const response = await axios.post(createApiUrl('/api/library/save-quiz'), {
        quizId: quizToSave._id,
        tags: tagsArray,
        category: libraryFormData.category,
        isPublic: libraryFormData.isPublic,
        isShared: libraryFormData.isShared
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setSuccess('Quiz saved to library successfully!');
        setShowSaveToLibraryModal(false);
        setQuizToSave(null);
      } else {
        setError('Failed to save quiz to library');
      }
    } catch (error) {
      console.error('Error saving quiz to library:', error);
      setError('Error saving quiz to library');
    }
  };

  const cancelSaveToLibrary = () => {
    setShowSaveToLibraryModal(false);
    setQuizToSave(null);
    setLibraryFormData({
      tags: '',
      category: 'academic',
      isPublic: false,
      isShared: false
    });
  };

  if (loading) {
    return <div className="text-center">Loading quizzes...</div>;
  }

  return (
    <div className={styles.quizManagement}>
      {error && error.trim() && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      {quizzes.length === 0 ? (
        <div className="text-center py-5">
          <p>No quizzes created yet.</p>
          <p className="text-muted">Use the 'Create New Quiz' button above to get started.</p>
        </div>
      ) : (
        <div className={styles.row}>
          {quizzes.map(quiz => (
            <div key={quiz._id} className={`${styles.colMd4} ${styles.mb4}`}>
              <div className={`${styles.card} h-100`}>
                <div className="card-body">
                  <h5 className={styles.cardTitle}>{quiz.title}</h5>
                  <p className={styles.cardText}>{quiz.description}</p>
                  <div className={styles.mb2}>
                    <small className={styles.textMuted}>
                      Subject: {quiz.subject} | Grade: {quiz.gradeLevel} | Difficulty: {quiz.difficulty}
                    </small>
                  </div>
                  <div className={styles.mb2}>
                    <small className={styles.textMuted}>
                      Duration: {quiz.estimatedDuration} min | Points: {quiz.totalPoints}
                    </small>
                  </div>
                  {quiz.assignedTo && quiz.assignedTo.length > 0 && (
                    <div className={styles.mb2}>
                      <small className={styles.textSuccess}>
                        Assigned to {quiz.assignedTo.length} student(s)
                      </small>
                    </div>
                  )}
                </div>
                <div className={styles.cardFooter}>
                  <div className={styles.buttonGrid}>
                    <button
                      className={`${styles.button} ${styles.btnInfo} ${styles.btnSm}`}
                      onClick={() => handleViewQuiz(quiz)}
                      title="View Quiz Details"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    <button
                      className={`${styles.button} ${styles.btnWarning} ${styles.btnSm}`}
                      onClick={() => handleEditQuiz(quiz)}
                      title="Edit Quiz"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className={`${styles.button} ${styles.btnPrimary} ${styles.btnSm}`}
                      onClick={() => {
                        setSelectedQuiz(quiz);
                        setSelectedStudentsForQuizAssignment([]); // Reset selected students
                        setAssignmentDueDate(''); // Reset due date
                        setSelectedYearGroup('all'); // Reset to "All year groups"
                        setShowAssignModal(true);
                      }}
                    >
                      Assign
                    </button>
                    <button
                      className={`${styles.button} ${styles.btnSecondary} ${styles.btnSm}`}
                      onClick={() => handleDuplicateQuiz(quiz)}
                    >
                      Duplicate
                    </button>
                    <button
                      className={`${styles.button} ${styles.btnLibrary} ${styles.btnSm}`}
                      onClick={() => handleSaveToLibrary(quiz)}
                      title="Save to Library"
                    >
                      <i className="fas fa-bookmark"></i>
                    </button>
                    <button
                      className={`${styles.button} ${styles.btnDanger} ${styles.btnSm}`}
                      onClick={() => handleDeleteQuiz(quiz)}
                      style={{ gridColumn: '1 / -1' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Quiz Modal */}
      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)}>
        <div className="modal-header">
          <h3>Assign Quiz: {selectedQuiz?.title}</h3>
          <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-3">
            <label className="form-label">Select Students</label>
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Filter by Year Group</label>
                <select
                  key={`year-group-${selectedYearGroup}`}
                  className={`form-select ${styles.yearGroupSelect}`}
                  value={selectedYearGroup}
                  onChange={(e) => {
                    setSelectedYearGroup(e.target.value);
                  }}
                >
                  <option value="all">All year groups</option>
                  {yearGroups.map(grade => (
                    <option key={grade} value={grade}>
                      Year {grade}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {/* Select All Checkbox */}
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="select-all-quiz-students"
                  checked={getFilteredStudents()
                    .filter(student => selectedYearGroup === 'all' || 
                      (student.yearGrade || '') === selectedYearGroup)
                    .every(student => 
                      selectedStudentsForQuizAssignment.find(s => s._id === student._id) !== undefined
                    )}
                  onChange={() => {
                    const filteredStudents = getFilteredStudents()
                      .filter(student => selectedYearGroup === 'all' || 
                        (student.yearGrade || '') === selectedYearGroup);
                    
                    const allSelected = filteredStudents.every(student => 
                      selectedStudentsForQuizAssignment.find(s => s._id === student._id) !== undefined
                    );
                    
                    if (allSelected) {
                      // Deselect all filtered students
                      const filteredStudentIds = filteredStudents.map(s => s._id);
                      setSelectedStudentsForQuizAssignment(prev => 
                        prev.filter(s => !filteredStudentIds.includes(s._id))
                      );
                    } else {
                      // Select all filtered students
                      const newSelections = filteredStudents.filter(student => 
                        !selectedStudentsForQuizAssignment.find(s => s._id === student._id)
                      );
                      setSelectedStudentsForQuizAssignment(prev => [...prev, ...newSelections]);
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="select-all-quiz-students">
                  <strong>Select All Students in {selectedYearGroup === 'all' ? 'All Year Groups' : `Year ${selectedYearGroup}`}</strong>
                </label>
              </div>
              
              {getFilteredStudents()
                .filter(student => selectedYearGroup === 'all' || 
                  (student.yearGrade || '') === selectedYearGroup)
                .map(student => (
                  <div key={student._id} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`quiz-assignment-student-${student._id}`}
                      checked={selectedStudentsForQuizAssignment.find(s => s._id === student._id) !== undefined}
                      onChange={() => handleStudentSelectionForQuizAssignment(student)}
                    />
                    <label className="form-check-label" htmlFor={`quiz-assignment-student-${student._id}`}>
                      {student.contactName}{student.yearGrade && ` (Year ${student.yearGrade})`}
                    </label>
                  </div>
                ))}
            </div>
            {selectedStudentsForQuizAssignment.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">
                  Selected: {selectedStudentsForQuizAssignment.length} student(s)
                </small>
              </div>
            )}
          </div>
          
          <div className="form-group mb-3">
            <label className="form-label">Due Date (Optional)</label>
            <input
              type="date"
              className="form-control"
              value={assignmentDueDate}
              onChange={(e) => setAssignmentDueDate(e.target.value)}
              style={{ maxWidth: '200px', height: '32px' }}
            />
          </div>
          
          <div className="d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => setShowAssignModal(false)}
              disabled={assigningQuiz}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleAssignQuiz}
              disabled={assigningQuiz || selectedStudentsForQuizAssignment.length === 0}
            >
              {assigningQuiz ? 'Assigning...' : 'Assign Quiz'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={cancelDeleteQuiz}>
        <div className="modal-header">
          <h3>Delete Quiz</h3>
          <button className="close-btn" onClick={cancelDeleteQuiz}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>
            Are you sure you want to delete the quiz <strong>"{quizToDelete?.title}"</strong>?
          </p>
          <p className="text-muted">
            This will permanently remove the quiz and all associated data.
          </p>
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={cancelDeleteQuiz}
              disabled={deletingQuiz}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-danger"
              onClick={confirmDeleteQuiz}
              disabled={deletingQuiz}
            >
              <i className="fas fa-trash me-2"></i>
              {deletingQuiz ? 'Deleting...' : 'Delete Quiz'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Quiz View Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)}>
        <div className="modal-header">
          <h3>Quiz Details</h3>
          <button className="close-btn" onClick={() => setShowViewModal(false)}>×</button>
        </div>
        <div className="modal-body">
          {selectedQuizForView && (
            <div>
              <h4>{selectedQuizForView.title}</h4>
              <p className="text-muted">{selectedQuizForView.description}</p>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Subject:</strong> {selectedQuizForView.subject}
                </div>
                <div className="col-md-6">
                  <strong>Grade Level:</strong> {selectedQuizForView.gradeLevel}
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Difficulty:</strong> {selectedQuizForView.difficulty}
                </div>
                <div className="col-md-6">
                  <strong>Duration:</strong> {selectedQuizForView.estimatedDuration} minutes
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Total Points:</strong> {selectedQuizForView.totalPoints}
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong> 
                  <span className={`badge ms-2 ${
                    selectedQuizForView.status === 'published' ? 'bg-success' : 
                    selectedQuizForView.status === 'draft' ? 'bg-warning' : 'bg-secondary'
                  }`}>
                    {selectedQuizForView.status}
                  </span>
                </div>
              </div>

              {selectedQuizForView.questions && selectedQuizForView.questions.length > 0 && (
                <div className="mt-4">
                  <h5>Questions ({selectedQuizForView.questions.length})</h5>
                  <div className="list-group">
                    {selectedQuizForView.questions.map((question, index) => (
                      <div key={question._id || index} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1">
                            <h6 className="mb-2">Question {index + 1} ({question.type}) - {question.points} points</h6>
                            <p className="mb-2">{question.question}</p>
                          </div>
                        </div>

                        {/* Question Options (for multiple choice) */}
                        {question.type === 'multiple-choice' && question.options && question.options.length > 0 && (
                          <div className="mb-3">
                            <strong>Options:</strong>
                            <ul className="list-unstyled mt-2">
                              {question.options.map((option, optIndex) => (
                                <li key={optIndex} className={`p-2 rounded mb-1 ${optIndex === question.correctAnswer ? 'bg-success text-white' : 'bg-light'}`}>
                                  <span className="me-2">{String.fromCharCode(65 + optIndex)}.</span>
                                  {option}
                                  {optIndex === question.correctAnswer && <span className="ms-2">✓ Correct Answer</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* True/False Answer */}
                        {question.type === 'true-false' && (
                          <div className="mb-3">
                            <strong>Correct Answer:</strong>
                            <div className={`p-2 rounded mt-2 ${question.correctAnswer ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                              {question.correctAnswer ? 'True' : 'False'} ✓
                            </div>
                          </div>
                        )}

                        {/* Fill in the Blank Answer */}
                        {question.type === 'fill-blank' && question.correctText && (
                          <div className="mb-3">
                            <strong>Correct Answer:</strong>
                            <div className="p-2 bg-success text-white rounded mt-2">
                              {question.correctText} ✓
                            </div>
                          </div>
                        )}

                        {/* Essay Question */}
                        {question.type === 'essay' && (
                          <div className="mb-3">
                            <strong>Essay Question</strong>
                            <div className="p-2 bg-info text-white rounded mt-2">
                              This is an essay question. Students will provide written responses.
                            </div>
                            {question.rubric && question.rubric.criteria && question.rubric.criteria.length > 0 && (
                              <div className="mt-2">
                                <strong>Grading Rubric:</strong>
                                <ul className="list-unstyled mt-2">
                                  {question.rubric.criteria.map((criteria, criteriaIndex) => (
                                    <li key={criteriaIndex} className="p-2 bg-light rounded mb-1">
                                      <strong>{criteria.name}:</strong> {criteria.description} ({criteria.maxPoints} points)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Explanation */}
                        {question.explanation && (
                          <div className="mb-3">
                            <strong>Explanation:</strong>
                            <div className="p-2 bg-light rounded mt-2">
                              {question.explanation}
                            </div>
                          </div>
                        )}

                        {/* Black History Connection */}
                        {question.blackHistoryConnection && (
                          <div className="mb-3">
                            <strong>Black History Connection:</strong>
                            <div className="p-3 bg-warning rounded mt-2">
                              <div><strong>Figure:</strong> {question.blackHistoryConnection.figure}</div>
                              <div><strong>Context:</strong> {question.blackHistoryConnection.context}</div>
                              <div><strong>Significance:</strong> {question.blackHistoryConnection.significance}</div>
                            </div>
                          </div>
                        )}

                        {/* Hints */}
                        {question.hints && question.hints.length > 0 && (
                          <div className="mb-3">
                            <strong>Hints:</strong>
                            <ul className="list-unstyled mt-2">
                              {question.hints.map((hint, hintIndex) => (
                                <li key={hintIndex} className="p-2 bg-light rounded mb-1">
                                  {hint}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Question Requirements */}
                        <div className="mt-3">
                          <small className="text-muted">
                            Required: {question.required ? 'Yes' : 'No'} | 
                            Order: {question.order}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedQuizForView.assignedTo && selectedQuizForView.assignedTo.length > 0 && (
                <div className="mt-4">
                  <h5>Assigned To ({selectedQuizForView.assignedTo.length} students)</h5>
                  <div className="list-group">
                    {selectedQuizForView.assignedTo.map((assignment, index) => (
                      <div key={index} className="list-group-item">
                        <div className="d-flex justify-content-between">
                          <span>
                            {assignment.studentId?.firstName} {assignment.studentId?.lastName}
                            {assignment.studentId?.contactName && ` (${assignment.studentId.contactName})`}
                          </span>
                          <span className="badge bg-info">{assignment.status}</span>
                        </div>
                        {assignment.dueDate && (
                          <small className="text-muted">
                            Due: {new Date(assignment.dueDate).toLocaleDateString()}
                          </small>
                        )}
                        {assignment.score !== undefined && (
                          <div className="mt-2">
                            <small className="text-success">
                              <strong>Score:</strong> {assignment.score}% 
                              {assignment.attempts > 0 && ` (${assignment.attempts} attempt${assignment.attempts > 1 ? 's' : ''})`}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Quiz Edit Modal */}
      <QuizCreationModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedQuizForEdit(null);
        }}
        onQuizCreated={handleQuizUpdated}
        editingQuiz={selectedQuizForEdit}
        isEditMode={true}
      />

      {/* Save to Library Modal */}
      <Modal isOpen={showSaveToLibraryModal} onClose={cancelSaveToLibrary}>
        <div className="modal-header">
          <h3>Save Quiz to Library</h3>
          <button className="close-btn" onClick={cancelSaveToLibrary}>×</button>
        </div>
        <div className="modal-body">
          {quizToSave && (
            <div>
              <div className="mb-3">
                <h5>{quizToSave.title}</h5>
                <p className="text-muted">{quizToSave.description}</p>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Tags (comma-separated)</label>
                <input
                  type="text"
                  className="form-control"
                  value={libraryFormData.tags}
                  onChange={(e) => handleLibraryFormChange('tags', e.target.value)}
                  placeholder="e.g., math, algebra, equations"
                />
                <small className="form-text text-muted">
                  Add tags to help you find this quiz later
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={libraryFormData.category}
                  onChange={(e) => handleLibraryFormChange('category', e.target.value)}
                >
                  <option value="academic">Academic</option>
                  <option value="assessment">Assessment</option>
                  <option value="practice">Practice</option>
                  <option value="review">Review</option>
                  <option value="homework">Homework</option>
                </select>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isPublic"
                    checked={libraryFormData.isPublic}
                    onChange={(e) => handleLibraryFormChange('isPublic', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isPublic">
                    Make this quiz public (visible to other teachers)
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isShared"
                    checked={libraryFormData.isShared}
                    onChange={(e) => handleLibraryFormChange('isShared', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isShared">
                    Share with school colleagues
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cancelSaveToLibrary}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmSaveToLibrary}
          >
            <i className="fas fa-bookmark me-2"></i>
            Save to Library
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default QuizManagement;